import classNames from "classnames";
import { useValidation } from "Hooks/useValidation";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button";
import ButtonAsyncState from "Views/Components/Button/ButtonAsyncState";
import { ButtonGroup } from "Views/Components/Button/ButtonGroup";
import EntityList from "Views/Components/EntityList/EntityList";
import Modal from "Views/Components/Modal/Modal";
import { DisplayType } from "Views/Components/Models/Enums";
import { NumberTextField } from "Views/Components/NumberTextBox/NumberTextBox";
import TablePagination from "Views/Components/Pagination/TablePagination";
import RadioButtonGroupSetter from "Views/Components/RadioButton/RadioButtonGroupSetter";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { useAppSettingStore } from "../SettingStores/AppSettingPageStore";
import { OrganisationProductSummary } from "../types/OrganisationProviderProductStoreTypes";
import { ComboboxOptions } from "Util/Constants";

type Props = {
	providerId: string;
	productId: string;
}

export const OrganisationProviderProductList: React.FunctionComponent<Props> = observer(({
	providerId,
	productId,
}) => {
	const { creditProviderProductStore: { organisationProductStore } } = useAppSettingStore();
	const [selectedOrganisationProviderProduct, setSelectedOrganisationProviderProduct] = useState<OrganisationProductSummary | undefined>(undefined);

	useEffect(() => {
		if (!!providerId && !!productId) {
			organisationProductStore.getAvailableOrganisations(providerId, productId);
		}

		return () => {
			organisationProductStore.reset();
		}
	}, [organisationProductStore, productId, providerId]);

	return (
		<>
			<ButtonGroup>
				<ButtonAsyncState
					colors={Colors.Primary}
					display={Display.Solid}
					onPress={async () => {
						await organisationProductStore.setBulkActionForAll(true, providerId, productId);
					}}
					readonly={organisationProductStore.isLoading}
				>
					Enable all
				</ButtonAsyncState>
				<ButtonAsyncState
					colors={Colors.Error}
					display={Display.Outline}
					readonly={organisationProductStore.isLoading}
					onPress={async () => {
						await organisationProductStore.setBulkActionForAll(false, providerId, productId);
					}}
				>
					Disable all
				</ButtonAsyncState>
			</ButtonGroup>

			<div className="collection-component">
				<EntityList
					collection={organisationProductStore.availableOrganisations}
					columns={[
						{
							displayName: "Organisation ID",
							columnName: "organisationId",
							value: item => item.organisationId,
							className: "field-organisationId",
							hide: true,
						},
						{
							displayName: "Organisation Provider Product Id",
							columnName: "organisationProductId",
							value: item => item.organisationProductId,
							className: "field-organisationProductId",
							hide: true,
						},
						{
							displayName: "Organisation Name",
							columnName: "organisationName",
							value: item => item.organisationName,
							className: "field-organisationName",
						},
						{
							displayName: "Actual Price",
							columnName: "price",
							value: item => `$${item.price.toFixed(2)}`,
							className: "field-price",
						},
						{
							displayName: "Markup",
							columnName: "customerMarkup",
							value: item => !!item.customMarkup ? `${item.customMarkup.toFixed(2)}%` : "-",
							className: "field-customerMarkup",
						},
						{
							displayName: "Discount Rate",
							columnName: "discountRate",
							value: item => !!item.discountRate ? `${item.discountRate.toFixed(2)}%` : "-",
							className: "field-discountRate",
						},
						{
							displayName: "Calculated Cost",
							columnName: "calculatedCost",
							value: item => {
								const markupPrice = (item.price + (item.price * (item.customMarkup / 100)));
								const discountAmount = markupPrice * (item.discountRate / 100);
								const calculatedCost = markupPrice - discountAmount;
								return `$${calculatedCost.toFixed(2)}`;
							}
						},
						{
							displayName: "Enabled",
							columnName: "enabled",
							value: item => (
								<span
									className={classNames("alert-box mini", { error: !item.isLinked, success: item.isLinked })}
									style={{ background: "none" }}
								/>
							),
							className: "field-enabled",
						},
						{
							displayName: "Recommended",
							columnName: "isRecommended",
							value: item => (
								<span
									className={classNames("alert-box mini", { error: !item.isRecommended, success: item.isRecommended })}
									style={{ background: "none" }}
								/>
							),
							className: "field-isRecommended",
						}
					]}
					idColumn="organisationId"
					sortColumn="organisationName"
					sortDescending
					onClickRow={item => setSelectedOrganisationProviderProduct(item)}
				/>
				<TablePagination
					perPage={organisationProductStore.pageSize}
					pageNo={organisationProductStore.pageNo - 1}
					totalRecords={organisationProductStore.totalCount}
					onPageChange={pageNo => organisationProductStore.setPageNo(pageNo + 1)}
				/>
				{!!selectedOrganisationProviderProduct && (
					<UpdateOrganisationProviderProductSection
						detail={selectedOrganisationProviderProduct}
						updateDetail={organisationProductStore.updateOrganisationProductDetail}
						onClose={() => setSelectedOrganisationProviderProduct(undefined)}
						isLoading={organisationProductStore.isLoading}
					/>
				)}
			</div>
		</>
	)
});

interface InnerProps {
	detail: OrganisationProductSummary;
	updateDetail: (data: OrganisationProductSummary) => Promise<void>;
	isLoading: boolean
	onClose: () => void;
}
export const UpdateOrganisationProviderProductSection: React.FunctionComponent<InnerProps> = observer(({
	detail,
	isLoading,
	updateDetail,
	onClose,
}) => {
	const organisationName = detail.organisationName;
	const productName = detail.productName;

	const [organisationProductSummary, setOrganisationProductSummary] = useState<OrganisationProductSummary>(useMemo(() => detail, [detail]));
	const validation = useValidation({
		price: {
			value: organisationProductSummary.price,
			isRequired: true,
			requiredText: "Price is required.",
			validate: value => value.assertNumberBetween(detail.baseCost, Number.MAX_VALUE, `Price can't be less then actual cost (${detail.baseCost}) of product`),
		},
		discountRate: {
			value: organisationProductSummary.discountRate,
			isRequired: true,
			requiredText: "Discount rate is required.",
			validate: value => value.assertNumberBetween(0, 100, "Discount rate must be between 1 and 100."),
		},
		customMarkup: {
			value: organisationProductSummary.customMarkup,
			isRequired: true,
			requiredText: "Custom markup is required.",
			validate: value => value.assertNumberBetween(0, 10000, "Custom markup must be between 1 and 10,000."),
		},
	}, true);

	return (
		<Modal
			isOpen={true}
			label="Update Organisation Provider Product"
		>
			<div className="modal__header">
				<h5>{productName} detail's for {organisationName}</h5>
				<Button
					className="modal-close"
					key="header-cancel"
					icon={{ icon: 'cross', iconPos: 'icon-only' }}
					display={Display.Text}
					onClick={onClose}
					sizes={Sizes.Medium}
					colors={Colors.Error}
					labelVisible={false}
					disabled={isLoading}
				>
					Cancel
				</Button>
			</div>
			<div className="modal__body">
				{!isLoading && (
					<>
						<RadioButtonGroupSetter
							value={organisationProductSummary.isLinked ? "yes" : "no"}
							label={`Enable this product for ${organisationName}`}
							options={ComboboxOptions.Bools}
							displayType={DisplayType.INLINE}
							setValue={value => setOrganisationProductSummary({ ...organisationProductSummary, isLinked: value === "yes" })}
						/>
						<NumberTextField
							label={`Enter base price`}
							model={organisationProductSummary}
							modelProperty="price"
							onAfterChange={event => setOrganisationProductSummary({ ...organisationProductSummary, price: parseFloat(event.target.value) })}
							tooltip="The base cost price of the product. By default this is the base cost price of the product from the provider."
							errors={validation.get("price")}
							isDisabled={!organisationProductSummary.isLinked}
							isReadOnly
						/>
						<NumberTextField
							label={`Add markup percentage to base price`}
							model={organisationProductSummary}
							modelProperty="customMarkup"
							isReadOnly
							tooltip="The custom markup for the product. This is the percentage that will be added to the base cost price of the product."
							errors={validation.get("customMarkup")}
							isDisabled={!organisationProductSummary.isLinked}
						/>
						<NumberTextField
							label={`Add discount percentage to markup value`}
							model={organisationProductSummary}
							modelProperty="discountRate"
							onAfterChange={event => setOrganisationProductSummary({ ...organisationProductSummary, discountRate: parseFloat(event.target.value) })}
							tooltip="The discount rate for the product. This is the percentage that will be subtracted from the base cost price of the product."
							errors={validation.get("discountRate")}
							isDisabled={!organisationProductSummary.isLinked}
						/>
						<RadioButtonGroupSetter
							value={organisationProductSummary.isRecommended ? "yes" : "no"}
							label="Mark product as recommended?"
							options={ComboboxOptions.Bools}
							displayType={DisplayType.INLINE}
							setValue={value => setOrganisationProductSummary({ ...organisationProductSummary, isRecommended: value === "yes" })}
							isDisabled={!organisationProductSummary.isLinked}
						/>
					</>
				)}
				{isLoading && (
					<>
						<InlineSpinner />
						<p className="alert mini alert-info">Updating the provider product detail...</p>
					</>
				)}
			</div>
			<ButtonGroup className="modal__actions">
				<Button
					display={Display.Outline}
					colors={Colors.Error}
					sizes={Sizes.Medium}
					onClick={onClose}
					disabled={isLoading}
				>
					Cancel
				</Button>
				<ButtonAsyncState
					display={Display.Solid}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onPress={async () => {
						await updateDetail(organisationProductSummary);
						onClose()
					}}
					readonly={!validation.isValid || isLoading}
				>
					Save
				</ButtonAsyncState>
			</ButtonGroup>
		</Modal>
	)
});